'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { alpha } from '@mui/material/styles';
import { Box, Button, Stack, Typography } from '@/primitives';
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import useRateLimitStore from '@/publicSearch/store/useRateLimitStore';
import useAnonymousSearchLimitStore, {
  getAnonymousSearchLimitResetAt,
  MAX_UNAUTH_SEARCHES,
} from '@/publicSearch/store/useAnonymousSearchLimitStore';
import { useResolvedColorScheme } from '@/hooks/useResolvedColorScheme';
import { signIn } from 'next-auth/react';
import { AUTH_PROVIDER } from '@/constants/auth';

/**
 * Banner displayed when an anonymous user has exceeded the search rate limit
 * (client-side daily cap or server-side GraphQL limit). Shows a countdown
 * timer and a sign-in CTA.
 */
export default function RateLimitBanner() {
  const { isDarkMode } = useResolvedColorScheme();
  const {
    isRateLimited: isServerRateLimited,
    resetsAt,
    limit,
    clearRateLimit,
  } = useRateLimitStore();
  const isClientLimited = useAnonymousSearchLimitStore((s) => s.isLimited);
  const syncFromStorage = useAnonymousSearchLimitStore(
    (s) => s.syncFromStorage,
  );
  const [timeRemaining, setTimeRemaining] = useState('');

  const isBlocked = isServerRateLimited || isClientLimited;

  const resetDate = useMemo(() => {
    if (isServerRateLimited && resetsAt) {
      return new Date(resetsAt);
    }
    if (isClientLimited) {
      return new Date(getAnonymousSearchLimitResetAt());
    }
    return null;
  }, [isClientLimited, isServerRateLimited, resetsAt]);

  useEffect(() => {
    syncFromStorage();
  }, [syncFromStorage]);

  useEffect(() => {
    if (!isBlocked || !resetDate) return;

    const updateCountdown = () => {
      const now = new Date();
      const diff = resetDate.getTime() - now.getTime();

      if (diff <= 0) {
        if (isServerRateLimited) {
          clearRateLimit();
        }
        if (isClientLimited) {
          syncFromStorage();
        }
        setTimeRemaining('');
        return;
      }

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

      if (hours > 0) {
        setTimeRemaining(`${hours}h ${minutes}m`);
      } else {
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);
        setTimeRemaining(`${minutes}m ${seconds}s`);
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [
    isBlocked,
    isClientLimited,
    isServerRateLimited,
    resetDate,
    clearRateLimit,
    syncFromStorage,
  ]);

  const handleSignIn = useCallback(() => {
    signIn(AUTH_PROVIDER);
  }, []);

  if (!isBlocked) return null;

  const guestSearchLimit = isClientLimited ? MAX_UNAUTH_SEARCHES : limit;
  const displayMessage = `You've used your ${guestSearchLimit} guest searches for today. Create a free account to keep searching — registration only takes a minute, and you won't see this screen again once you're signed in.`;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 1,
        minHeight: 0,
        p: 4,
        textAlign: 'center',
      }}
    >
      <Box
        sx={(theme) => ({
          maxWidth: 520,
          width: '100%',
          borderRadius: 3,
          border: '1px solid',
          borderColor: theme.vars?.palette?.primary?.mainChannel
            ? `rgba(${theme.vars.palette.primary.mainChannel} / ${isDarkMode ? 0.45 : 0.35})`
            : alpha(theme.palette.primary.main, isDarkMode ? 0.45 : 0.35),
          // Token strings follow `data-mui-color-scheme`; do not use theme.palette.* here —
          // with colorSchemes + cssVariables the JS palette often stays on light values.
          bgcolor: 'background.paper',
          color: 'text.primary',
          boxShadow: isDarkMode
            ? `0 4px 12px ${alpha(theme.palette.common.black, 0.4)}`
            : `0 1px 3px ${alpha(theme.palette.common.black, 0.08)}`,
          p: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
        })}
      >
        <Box
          sx={(theme) => ({
            width: 56,
            height: 56,
            borderRadius: '50%',
            backgroundColor: theme.vars?.palette?.primary?.mainChannel
              ? `rgba(${theme.vars.palette.primary.mainChannel} / ${isDarkMode ? 0.22 : 0.1})`
              : alpha(theme.palette.primary.main, isDarkMode ? 0.22 : 0.1),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 1,
          })}
        >
          <PersonAddOutlinedIcon sx={{ fontSize: 28, color: 'primary.main' }} />
        </Box>

        <Typography
          variant="titlemedium"
          sx={{
            color: 'text.primary',
            fontWeight: 600,
          }}
        >
          Create a free account to keep searching
        </Typography>

        <Typography
          variant="bodymedium"
          sx={{
            color: 'text.secondary',
            maxWidth: 440,
            lineHeight: 1.6,
          }}
        >
          {displayMessage}
        </Typography>

        {timeRemaining && (
          <Stack
            direction="row"
            spacing={0.5}
            sx={{
              alignItems: 'center',
              color: 'text.secondary',
            }}
          >
            <AccessTimeIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            <Typography
              variant="bodysmall"
              sx={{
                color: 'text.secondary',
              }}
            >
              Guest access resets in {timeRemaining}
            </Typography>
          </Stack>
        )}

        <Button
          variant="contained"
          size="large"
          onClick={handleSignIn}
          sx={{ mt: 1, minWidth: 220 }}
        >
          Sign in — it&apos;s free
        </Button>
      </Box>
    </Box>
  );
}
